import React, { useState, useEffect, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";
import { Box, Typography, Paper, Chip, Divider, CircularProgress, Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from "@material-ui/core";
import NotificationsActiveOutlinedIcon from "@material-ui/icons/NotificationsActiveOutlined";
import AlternateEmailOutlinedIcon from "@material-ui/icons/AlternateEmailOutlined";
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";
import StopIcon from "@material-ui/icons/Stop";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import moment from "moment";
import { cancelCampaign, stopCampaign, restartCampaign as restartCampaignApi } from "../../services/campaigns.api";
import { getContact360Messages } from "../../services/contacts.api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { D11 } from "./contact360.constants";

// D8.4.4c — Tab "Mensajes" (auto-contenido: estado + API propios; recibe classes + contactId).

function campaignStatusChipStyle(status) {
  const s = (status || "").toUpperCase();
  if (s === "PROGRAMADA") return { borderColor: "#1976d2", color: "#1565c0", backgroundColor: "rgba(25,118,210,0.08)" };
  if (s === "EM_ANDAMENTO") return { borderColor: D11.green, color: "#2e7d32", backgroundColor: "rgba(0,200,83,0.1)" };
  if (s === "CANCELADA") return { borderColor: "#ef5350", color: "#c62828", backgroundColor: "rgba(211,47,47,0.08)" };
  if (s === "FINALIZADA") return { borderColor: D11.green, color: "#1b5e20", backgroundColor: "rgba(0,200,83,0.06)" };
  if (s === "INATIVA") return { borderColor: "#bdbdbd", color: "#757575", backgroundColor: "#f5f5f5" };
  return { borderColor: "#e0e0e0", color: "#424242", backgroundColor: "#fafafa" };
}

export default function Contact360MensajesTab({ classes, contactId }) {
  const history = useHistory();
  const mountedRef = React.useRef(true);
  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);
  const [pauseTarget, setPauseTarget] = useState(null);
  const [stopTarget, setStopTarget] = useState(null);
  const [mutatingId, setMutatingId] = useState(null);

  const fetchData = useCallback(
    async (forceRefresh) => {
      if (!contactId) return;
      try {
        setLoading(true);
        const { data } = await getContact360Messages(contactId, {
          params: forceRefresh ? { refresh: true } : {}
        });
        if (!mountedRef.current) return;
        setPayload(data);
      } catch (e) {
        if (!mountedRef.current) return;
        toastError(e);
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    },
    [contactId]
  );

  useEffect(() => {
    fetchData(false);
  }, [fetchData]);

  const kpis = payload?.kpis || {};
  const secuencias = Array.isArray(payload?.secuencias) ? payload.secuencias : [];
  const campanas = Array.isArray(payload?.campanas) ? payload.campanas : [];
  const canManageCampaigns = !!payload?.capabilities?.canManageCampaigns;
  const fromCache = !!payload?.meta?.fromCache;

  const refreshAfterMutation = useCallback(async () => {
    await fetchData(true);
  }, [fetchData]);

  const confirmPause = async () => {
    if (!pauseTarget?.id) return;
    try {
      setMutatingId(pauseTarget.id);
      await cancelCampaign(pauseTarget.id);
      if (!mountedRef.current) return;
      toast.success("Campaña pausada");
      setPauseTarget(null);
      await refreshAfterMutation();
    } catch (e) {
      toastError(e);
    } finally {
      if (mountedRef.current) setMutatingId(null);
    }
  };

  const confirmStop = async () => {
    if (!stopTarget?.id) return;
    try {
      setMutatingId(stopTarget.id);
      await stopCampaign(stopTarget.id);
      if (!mountedRef.current) return;
      toast.success("Campaña detenida");
      setStopTarget(null);
      await refreshAfterMutation();
    } catch (e) {
      toastError(e);
    } finally {
      if (mountedRef.current) setMutatingId(null);
    }
  };

  const restartCampaign = async (campaign) => {
    if (!campaign?.id) return;
    try {
      setMutatingId(campaign.id);
      await restartCampaignApi(campaign.id);
      if (!mountedRef.current) return;
      toast.success("Campaña reanudada");
      await refreshAfterMutation();
    } catch (e) {
      toastError(e);
    } finally {
      if (mountedRef.current) setMutatingId(null);
    }
  };

  return (
    <Box mt={2}>
      <Paper className={classes.bannerIa} elevation={0} style={{ marginBottom: 12 }}>
        <Typography variant="body2">
          Resumen operativo del canal WhatsApp para este número: KPIs de mensajes, tiempo medio de primera respuesta
          humana, envíos programados (secuencia) y participación en campañas masivas. Los datos KPI usan cache de 15 min
          (excepto tras actualizar o tras pausar / detener campañas).
        </Typography>
      </Paper>

      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" style={{ gap: 8 }} mb={1}>
        <Typography variant="subtitle2" style={{ fontWeight: 700, letterSpacing: 0.6, color: D11.blue }}>
          INDICADORES MENSAJERÍA
        </Typography>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          {fromCache ? (
            <Typography variant="caption" style={{ color: D11.placeholder }}>
              Cache TTL 15 min
            </Typography>
          ) : null}
          <Button variant="text" color="primary" size="small" disabled={loading} onClick={() => fetchData(true)}>
            Actualizar KPIs
          </Button>
        </Box>
      </Box>

      {loading && !payload ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={36} />
        </Box>
      ) : (
        <Box display="flex" flexWrap="wrap" style={{ gap: 10 }} mb={2}>
          <Paper className={classes.kpiPaper} variant="outlined">
            <Typography variant="caption">Enviados (humano)</Typography>
            <Typography className={classes.kpiValue}>
              {kpis.mensajesEnviados != null ? kpis.mensajesEnviados : "—"}
            </Typography>
          </Paper>
          <Paper className={classes.kpiPaper} variant="outlined">
            <Typography variant="caption">Automáticos</Typography>
            <Typography className={classes.kpiOrange}>
              {kpis.mensajesAutomaticos != null ? kpis.mensajesAutomaticos : "—"}
            </Typography>
          </Paper>
          <Paper className={classes.kpiPaper} variant="outlined">
            <Typography variant="caption">Conversaciones</Typography>
            <Typography className={classes.kpiGreen}>
              {kpis.conversacionesTotales != null ? kpis.conversacionesTotales : "—"}
            </Typography>
          </Paper>
          <Paper className={classes.kpiPaper} variant="outlined">
            <Typography variant="caption">TMR 1ª respuesta</Typography>
            <Typography className={classes.kpiGreen} variant="subtitle2">
              {kpis.tiempoMedioRespuestaLabel || "Sin datos"}
            </Typography>
          </Paper>
        </Box>
      )}

      <Divider style={{ margin: "14px 0" }} />

      <Box display="flex" alignItems="center" mb={1} style={{ gap: 8 }}>
        <NotificationsActiveOutlinedIcon style={{ fontSize: 22, color: D11.blue }} />
        <Typography variant="subtitle2" style={{ fontWeight: 700, letterSpacing: 0.6, color: D11.blue }}>
          SECUENCIAS / RECORDATORIOS (Schedule)
        </Typography>
      </Box>
      {secuencias.length === 0 ? (
        <Typography variant="body2" style={{ fontStyle: "italic", color: D11.placeholder, marginBottom: 12 }}>
          Sin envíos programados para este contacto.
        </Typography>
      ) : (
        secuencias.map((s) => (
          <Paper key={s.id} variant="outlined" style={{ padding: 12, marginBottom: 10, borderRadius: 8 }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" style={{ gap: 8 }}>
              <Typography variant="caption" style={{ color: D11.placeholder }}>
                #{s.id} · Envío previsto{" "}
                {s.sendAt ? moment(s.sendAt).format("DD/MM/YYYY HH:mm") : "—"}
              </Typography>
              <Chip size="small" label={s.badge || "Otro"} style={{ height: 24 }} variant="outlined" />
            </Box>
            {s.sentAt ? (
              <Typography variant="caption" display="block" style={{ color: D11.green }}>
                Entregado {moment(s.sentAt).format("DD/MM/YYYY HH:mm")}
              </Typography>
            ) : null}
            <Typography variant="body2" style={{ marginTop: 8, wordBreak: "break-word", color: "#424242" }}>
              {typeof s.preview === "string" ? s.preview : "—"}
            </Typography>
            <Typography variant="caption" style={{ color: D11.placeholder }}>
              Estado sistema: {s.status || "—"}
            </Typography>
          </Paper>
        ))
      )}

      <Divider style={{ margin: "18px 0" }} />

      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <AlternateEmailOutlinedIcon style={{ fontSize: 22, color: D11.blue }} />
          <Typography variant="subtitle2" style={{ fontWeight: 700, letterSpacing: 0.6, color: D11.blue }}>
            CAMPAÑAS (por número WhatsApp)
          </Typography>
        </Box>
        <Button size="small" color="primary" variant="text" onClick={() => history.push("/campaigns")}>
          Ir a Campañas
        </Button>
      </Box>
      {!canManageCampaigns ? (
        <Typography variant="caption" display="block" style={{ marginTop: 4, marginBottom: 12, color: D11.placeholder }}>
          Las acciones Pausar / Parar / Reanudar solo están disponibles para administradores.
        </Typography>
      ) : null}
      {campanas.length === 0 ? (
        <Typography variant="body2" style={{ fontStyle: "italic", color: D11.placeholder, marginTop: 8 }}>
          Sin envíos agrupados en campañas para este número en la empresa.
        </Typography>
      ) : (
        campanas.map((c) => (
          <Paper key={`camp-${c.id}`} variant="outlined" style={{ padding: 12, marginTop: 10, borderRadius: 8 }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" style={{ gap: 10 }}>
              <Box flex={1} minWidth={200}>
                <Typography variant="subtitle2" style={{ fontWeight: 700, color: D11.blue }}>
                  {typeof c.name === "string" ? c.name : `Campaña #${c.id}`}
                </Typography>
                <Typography variant="caption" style={{ display: "block", color: D11.placeholder }}>
                  ID {c.id} · Ejec./envíos relacionados ~{typeof c.shippingCount === "number" ? c.shippingCount : 0}{" "}
                  {c.lastActivityAt ? `· Últ. actividad ${moment(c.lastActivityAt).format("DD/MM/YYYY HH:mm")}` : ""}
                </Typography>
              </Box>
              <Chip
                label={typeof c.status === "string" ? c.status.replace(/_/g, " ") : "—"}
                size="small"
                variant="outlined"
                style={{ height: 26, ...campaignStatusChipStyle(c.status) }}
              />
            </Box>
            {canManageCampaigns ? (
              <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 4, marginTop: 10 }}>
                {String(c.status || "").toUpperCase() === "EM_ANDAMENTO" ? (
                  <IconButton
                    aria-label="Pausar campaña"
                    size="small"
                    title="Pausar campaña"
                    disabled={mutatingId === c.id}
                    onClick={() => setPauseTarget({ id: c.id, name: c.name })}
                  >
                    <PauseCircleOutlineIcon style={{ fontSize: 26, color: "#1976d2" }} />
                  </IconButton>
                ) : null}
                {(String(c.status || "").toUpperCase() === "PROGRAMADA" ||
                  String(c.status || "").toUpperCase() === "EM_ANDAMENTO") && (
                  <IconButton
                    aria-label="Parar campaña definitivamente"
                    size="small"
                    title="Parar campaña definitivamente"
                    disabled={mutatingId === c.id}
                    onClick={() => setStopTarget({ id: c.id, name: c.name })}
                  >
                    <StopIcon style={{ fontSize: 24, color: "#d32f2f" }} />
                  </IconButton>
                )}
                {String(c.status || "").toUpperCase() === "CANCELADA" &&
                typeof c.nextScheduledAt === "string" &&
                c.nextScheduledAt.trim() !== "" ? (
                  <IconButton
                    aria-label="Reanudar campaña"
                    size="small"
                    title="Reanudar campaña"
                    disabled={mutatingId === c.id}
                    onClick={() => restartCampaign(c)}
                  >
                    <PlayCircleOutlineIcon style={{ fontSize: 28, color: D11.green }} />
                  </IconButton>
                ) : null}
              </Box>
            ) : null}
          </Paper>
        ))
      )}

      <Dialog open={!!pauseTarget} onClose={() => !mutatingId && setPauseTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Pausar campaña</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            ¿Pausar esta campaña? Los mensajes ya enviados no se revierten. Podrá reanudarla después si corresponde.
          </Typography>
          {pauseTarget ? (
            <Typography variant="subtitle2" style={{ marginTop: 16 }}>
              {pauseTarget.name}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setPauseTarget(null)} disabled={mutatingId != null}>
            Cancelar
          </Button>
          <Button color="primary" variant="contained" onClick={confirmPause} disabled={mutatingId != null}>
            {mutatingId ? <CircularProgress size={22} /> : "Pausar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!stopTarget} onClose={() => !mutatingId && setStopTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle style={{ color: "#d32f2f" }}>Parar campaña definitivamente</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 8 }}>
            Acción muy restrictiva: no siempre permite reactivar desde aquí según configuración recurrente / estados del
            motor.
          </Typography>
          {stopTarget ? (
            <Typography variant="subtitle2">{stopTarget.name}</Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setStopTarget(null)} disabled={mutatingId != null}>
            Volver
          </Button>
          <Button
            variant="contained"
            style={{ backgroundColor: "#d32f2f", color: "#fff" }}
            onClick={confirmStop}
            disabled={mutatingId != null}
          >
            {mutatingId ? <CircularProgress size={22} /> : "Parar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
